// src/components/upmode-agent/email-confirm-card.tsx
//
// The other place something leaves Upmode.
//
// Not modelled by the DC — it is on the spec's §6 "not read when these designs
// were made" list — so it is restyled to the same vocabulary as its sibling,
// `publish-confirm-card`, and nothing about its behaviour moved: the same
// three fields, the same edit toggle, the same address validation, the same
// two handlers.
//
// It shares `.upm-approve` with the publish card deliberately. These are the
// same moment — a thing is about to leave, and you are being asked first — and
// two different-looking cards for one moment is how the second one stops being
// read as an approval at all.

import { useState } from "react";
import { AlertTriangle, Edit3, Send, X } from "lucide-react";
import { Button, Icon, IconButton, TextInput, Textarea } from "../kit";

export interface PendingEmail {
  tool_use_id: string;
  to_email: string;
  subject: string;
  body: string;
}

interface EmailConfirmCardProps {
  email: PendingEmail;
  onConfirm: (edited?: Partial<PendingEmail>) => void;
  onCancel: () => void;
}

function isValidEmail(addr: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr.trim());
}

export function EmailConfirmCard({ email, onConfirm, onCancel }: EmailConfirmCardProps) {
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(email.to_email);
  const [subject, setSubject] = useState(email.subject);
  const [body, setBody] = useState(email.body);

  const emailValid = isValidEmail(to);

  const handleSend = () => {
    if (!emailValid) return;
    onConfirm({ to_email: to.trim(), subject: subject.trim(), body: body.trim() });
  };

  return (
    <div className="upm-approve">
      <div className="upm-approve__rule" />
      <div className="upm-approve__inner">
        <div className="upm-approve__mark">
          <span className="upm-approve__rocket">
            <Icon icon={Send} size="sm" />
          </span>
          <span className="upm-adv-eyebrow upm-adv-eyebrow--brand">Waiting on you</span>
        </div>

        <div className="upm-approve__head">
          <h4 className="upm-approve__title">Ready to send</h4>
          <IconButton
            icon={Edit3}
            label={editing ? "Stop editing" : "Edit before sending"}
            onClick={() => setEditing(!editing)}
          />
        </div>

        <p className="upm-approve__summary">
          The agent has drafted an email. Review it, edit it if you need to, then approve.
        </p>

        <div className="upm-approve__editor">
          <div className="upm-adv-eyebrow">To</div>
          {editing ? (
            <TextInput
              type="email"
              value={to}
              placeholder="recipient@example.com"
              aria-label="Recipient email address"
              state={emailValid ? undefined : "error"}
              onChange={(e) => setTo(e.target.value)}
            />
          ) : (
            <div className="upm-approve__readtitle">
              {to || <span className="upm-approve__absent">No address</span>}
            </div>
          )}
          {!emailValid && (
            <span className="upm-approve__limit upm-approve__limit--hard">
              <Icon icon={AlertTriangle} size="sm" />
              That is not a valid email address
            </span>
          )}

          <div className="upm-adv-eyebrow">Subject</div>
          {editing ? (
            <TextInput
              value={subject}
              aria-label="Email subject"
              onChange={(e) => setSubject(e.target.value)}
            />
          ) : (
            <div className="upm-approve__readtitle">
              {subject || <span className="upm-approve__absent">No subject</span>}
            </div>
          )}

          <div className="upm-adv-eyebrow">Message</div>
          {editing ? (
            <Textarea
              value={body}
              rows={8}
              aria-label="Email body"
              onChange={(e) => setBody(e.target.value)}
            />
          ) : (
            <div className="upm-approve__readbody">
              {body || <span className="upm-approve__absent">Nothing drafted</span>}
            </div>
          )}
        </div>

        <div className="upm-approve__actions">
          <Button icon={Send} onClick={handleSend} disabled={!emailValid}>
            Send email
          </Button>
          <Button variant="secondary" icon={X} onClick={onCancel}>
            Cancel
          </Button>
          <span className="upm-approve__promise">Nothing leaves Upmode without your approval.</span>
        </div>
      </div>
    </div>
  );
}
